import { Loader } from '../Widgets';
import React, { useState, useEffect } from 'react';
import { Supervisor } from 'service';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import ArthaGateway from 'service/ArthaGateway';
import moment from 'moment';
import { get } from 'http';

export const ProductionReport = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [supervisor, setSupervisor] = useState<Supervisor>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [productions, setProductions] = useState<any[]>([]);
    const [durationId, setDurationId] = useState<string>("0");
    const [from, setFrom] = useState<string>(moment().format("YYYY-MM-DD"));
    const [to, setTo] = useState<string>(moment().format("YYYY-MM-DD"));
    const [brandId, setBrandId] = useState<number>(0);
    const [brands, setBrands] = useState<any[]>([]);
    const [groupId, setGroupId] = useState<number>(0);
    const [groups, setGroups] = useState<any[]>([]);
    const [masterId, setMasterId] = useState<number>(0);
    const [masters, setMasters] = useState<any[]>([]);
    const [productionStages, setProductionStages] = useState<any[]>([]);
    const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
    const [applicableSupervisors, setApplicableSupervisors] = useState<Supervisor[]>([]);
    const [stageId, setStageId] = useState<number>(2);
    const [supervisorId, setSupervisorId] = useState<number>(0);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(1000);
    const [totalPages, setTotalPages] = useState<number>(5);

    useEffect(() => {
        loadProductions();
    }, [pageNumber]);

    useEffect(() => {
        if (user.profile) {
            setSupervisor(user.profile.profile);
            let stages = [
                { id: 2, name: 'Cutting', isActive: true },
                { id: 5, name: 'Loading', isActive: true },
                { id: 6, name: 'Stiching', isActive: true },
                { id: 8, name: 'Kaja Button', isActive: true },
                { id: 9, name: 'Trimming', isActive: true },
                { id: 10, name: 'Ironing', isActive: true },
                { id: 12, name: 'Packing', isActive: true }
            ]
            setProductionStages(stages);

            loadBrands();
            loadSupervisors();
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        let applicableSup: Supervisor[] = [];
        supervisors.forEach((sup) => {
            if (sup && sup.stages.includes(stageId)) {
                applicableSup.push(sup);
            }
        });
        setApplicableSupervisors(applicableSup);
    }, [supervisors, stageId]);

    const loadSupervisors = () => {
        let cacheData = sessionStorage.getItem('SUPERVISORS_CACHE');
        if (cacheData && cacheData.length > 0) {
            let supervisorsData = JSON.parse(cacheData);
            setSupervisors(supervisorsData);
        }
        ArthaGateway.loadSupervisors().then((data) => {
            setSupervisors(data);
            sessionStorage.setItem('SUPERVISORS_CACHE', JSON.stringify(data));
        });
    }

    const loadBrands = () => {
        ArthaGateway.getAllBrands().then((data) => {
            setBrands(data);
        });
    }

    useEffect(() => {
        setGroups([]);
        if (brandId > 0) {
            ArthaGateway.getAllGroups(brandId).then((data) => {
                setGroups(data);
            });
        }
        else {
            setMasterId(0);
            setGroupId(0);
        }
    }, [brandId]);

    useEffect(() => {
        setMasters([]);
        if (brandId > 0 && groupId > 0) {
            ArthaGateway.getAllMasters(brandId, groupId).then((data) => {
                setMasters(data);
            });
        }
        else {
            setMasterId(0);
        }
    }, [groupId]);

    useEffect(() => {
        setProductions([]);
        if (pageNumber > 1) {
            setPageNumber(1);
        }
        else {
            loadProductions();
        }
    }, [durationId, brandId, groupId, masterId, stageId, supervisorId]);

    function loadProductions(): void {
        setIsLoading(true);
        setProductions([]);
        ArthaGateway.getProductions(durationId, from, to, brandId, groupId, masterId, stageId, supervisorId, null, pageNumber, pageSize).then((data) => {
            data.entries.map((production) => production.isExpanded = false);
            if (pageNumber == 1) {
                setProductions(data.entries);
            }
            else {
                setProductions([...productions, ...data.entries]);
            }
            setTotalPages(data.totalPages);
            setIsLoading(false);
        });
    }

    function shareReport(): void {
        let summary = getSupervisorSummary(productions);
        const summaryString = Object.entries(summary)
            .map(([name, total]) => `${name}:   *${total.toLocaleString()}*\n`)
            .join('');


        const totalQuantity = productions.reduce((acc, curr) => acc + curr.quantity, 0);
        const totalString = `Total Quantity: *${totalQuantity.toLocaleString()}*\n`;

        let reportUrl = `${window.location.origin}/r/p?d=${durationId}&b=${brandId}&g=${groupId}&m=${masterId}&s=${stageId}&su=${supervisorId}`;
        let reportContent =
            `*Production Report*\n` +
            `Duration: *${getDurationName()}*\n` +
            `Stage: *${getStageName()}*\n\n` +
            `${summaryString}\n` +
            `${totalString}\n` +
            `Link to full report: ${reportUrl}`;

        const encodedMessage = encodeURIComponent(reportContent);
        const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;

        window.open(whatsappUrl, '_blank');
    }

    function getStageName(): string {
        const stage = productionStages.find(stage => stage.id === stageId);
        return stage ? stage.name : 'All Stages';
    }

    function getDurationName(): string {
        switch (durationId) {
            case "0": return moment().format("DD/MMM/YYYY");
            case "1": return moment().subtract(1, 'days').format("DD/MMM/YYYY");
            case "2": return `Week of ${moment().startOf('week').format("DD/MMM/YYYY")} - ${moment().endOf('week').format("DD/MMM/YYYY")}`;
            case "3": return `Week of ${moment().subtract(1, 'weeks').startOf('week').format("DD/MMM/YYYY")} - ${moment().subtract(1, 'weeks').endOf('week').format("DD/MMM/YYYY")}`;
            case "4": return `Month of ${moment().startOf('month').format("DD/MMM/YYYY")} - ${moment().endOf('month').format("DD/MMM/YYYY")}`;
            case "5": return `Month of ${moment().subtract(1, 'months').startOf('month').format("DD/MMM/YYYY")} - ${moment().subtract(1, 'months').endOf('month').format("DD/MMM/YYYY")}`;
            case "6": return `Last 2 Month`;
            case "7": return `Last 3 Month`;
            case "8": return `Last Year`;
            case "9": return `Last 1 Year`;
            default: return `${moment(from).format("DD/MMM/YYYY")} to ${moment(to).format("DD/MMM/YYYY")}`;
        }
    }

    function getSupervisorSummary(data: any[]): Record<string, number> {
        return data.reduce((summary, item) => {
            const name = item.supervisorName;
            const qty: number = item.quantity || 0;

            if (!summary[name]) {
                summary[name] = 0;
            }
            summary[name] += qty;

            return summary;
        }, {} as Record<string, number>);
    }

    return (
        <>
            <div className="page-reports">
                <div className='row'>
                    <div className='col-6 col-md-4'>
                        <div className="form-group">
                            <label>Duration</label>
                            <select className="form-control" id="duration" value={durationId} onChange={(e) => setDurationId(e.target.value)}>
                                <option value="2024">FY 2024-2025</option>
                                <option value="2023">FY 2023-2024</option>
                                <option value="2022">FY 2022-2023</option>
                                <option value="2021">FY 2021-2022</option>
                                <option value="2020">FY 2020-2021</option>
                                <option value="2019">FY 2019-2020</option>
                                <option value="2018">FY 2018-2019</option>
                                <option value="0">Today</option>
                                <option value="1">Yesterday</option>
                                <option value="2">This Week</option>
                                <option value="3">Last Week</option>
                                <option value="4">This Month</option>
                                <option value="5">Last Month</option>
                                <option value="6">Last 2 Month</option>
                                <option value="7">Last 3 Month</option>
                                <option value="8">Last Year</option>
                                <option value="9">Last 1 Year</option>
                            </select>
                        </div>
                    </div>

                    <div className='col-6 col-md-4'>
                        <div className="form-group">
                            <label>Stage</label>
                            <select className="form-control" id="duration" value={stageId} onChange={(e) => setStageId(parseInt(e.target.value))}>
                                {productionStages.map((stage) => (
                                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className='col-6 col-md-4'>
                        <div className="form-group">
                            <label>Supervisor</label>
                            <select className="form-control" id="supervisor" value={supervisorId} onChange={(e) => setSupervisorId(parseInt(e.target.value))}>
                                <option value="0">All</option>
                                {applicableSupervisors.map((supervisor) => (
                                    <option key={supervisor.id} value={supervisor.id}>{supervisor.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className='col-6 col-md-4'>
                        <div className='btn btn-primary btn-sm mt-2 mb-2' onClick={() => loadProductions()}>Refresh</div>
                    </div>
                </div>
                <div className="form-group">
                    <label>Brand</label>
                    <select className="form-control" value={brandId} onChange={(e) => setBrandId(parseInt(e.target.value))}>
                        <option value="0">All</option>
                        {brands.map((brand) => (
                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                        ))}
                    </select>
                </div>
                {brandId > 0 &&
                    <div className="form-group">
                        <label>Group</label>
                        <select className="form-control" value={groupId} onChange={(e) => setGroupId(parseInt(e.target.value))}>
                            <option value="0">All</option>
                            {groups.map((group) => (
                                <option key={group.id} value={group.id}>{group.name}</option>
                            ))}
                        </select>
                    </div>
                }
                {groupId > 0 &&
                    <div className="form-group">
                        <label>Master</label>
                        <select className="form-control" value={masterId} onChange={(e) => setMasterId(parseInt(e.target.value))}>
                            <option value="0">All</option>
                            {masters.map((master) => (
                                <option key={master.id} value={master.id}>{master.name}</option>
                            ))}
                        </select>
                    </div>
                }
                <div className="ui-divider" />
                <div className="list-group mt-2 mb-2">
                    {productions && productions.map((jo) => (
                        <div key={jo.jobId} className='list-group-item list-group-item-action'>
                            <div className='float-start'>
                                <b>{jo.jobOrderNumber}</b>
                                <div>{jo.master}</div>
                                <div>{jo.supervisorName}</div>
                                <div>{moment(jo.date).format("DD/MMM/YYYY")}</div>
                            </div>
                            <div className="float-end fw-bold" style={{ fontSize: '1.8rem' }}>{jo.quantity.toLocaleString()}</div>
                        </div>
                    ))}
                    <div className='list-group-item bg-dark text-light'>
                        <div className='float-start'>
                            Total
                        </div>
                        <div className="float-end fw-bold " style={{ fontSize: '1.8rem' }}>
                            {productions.reduce((acc, curr) => acc + curr.quantity, 0).toLocaleString()}
                        </div>
                    </div>
                </div>

                {(totalPages > pageNumber) &&
                    <>
                        {isLoading &&
                            <div className="btn btn-dark w-100 text-center">
                                <div className="spinner-border text-light" role="status">
                                </div>
                            </div>
                        }
                        {!isLoading &&
                            <div className="btn btn-dark w-100 text-center" onClick={() => setPageNumber(pageNumber + 1)}>Load More...</div>
                        }
                    </>
                }

                {productions.length > 0 &&
                    <div className='btn btn-sm bg-primary text-white text-center mt-2 mb-2' onClick={() => shareReport()}>Share Report</div>
                }
            </div>
        </>
    );
};